import { Table, Input } from "antd";
import { useMemo, useState } from "react";
/** @type {import('antd/es/table/interface').TablePaginationConfig} */

/**
 * Dynamic table that accepts columns + dataSource (matrix from hook).
 * Adds local search on the entity column.
 * Also enforces min column widths so long week labels don't wrap.
 */
export default function EngagementTable({ columns, dataSource }) {
    const [search, setSearch] = useState("");

    const filtered = useMemo(() => {
        if (!search) return dataSource;
        const q = search.toLowerCase();
        return dataSource.filter((r) => String(r.entity).toLowerCase().includes(q));
    }, [dataSource, search]);

    // Set minimum widths: wider for the first (entity) column,
    // and a generous width for time buckets (fits "1 Jan–7 Jan" / "2025-W09").
    const ENTITY_MIN = 220;
    const TIME_MIN = 140;

    const columnsWithMinWidths = useMemo(
        () =>
            (columns || []).map((col, idx) => {
                const isEntity = col.dataIndex === "entity" || idx === 0;
                const min = isEntity ? ENTITY_MIN : TIME_MIN;

                return {
                    ...col,
                    // Respect any width you already set; otherwise fall back to our minimum
                    width: col.width ?? min,
                    onHeaderCell: () => ({
                        style: {
                            minWidth: min,        // key: ensures header cell never shrinks below this
                            whiteSpace: "nowrap", // keep week labels on one line
                        },
                    }),
                    onCell: () => ({
                        style: {
                            minWidth: min,
                            whiteSpace: "nowrap",
                        },
                    }),
                };
            }),
        [columns]
    );

    return (
        <>
            <Input.Search
                placeholder="Search…"
                allowClear
                style={{ maxWidth: 360, marginBottom: 12 }}
                onChange={(e) => setSearch(e.target.value)}
            />

            <Table
                columns={columnsWithMinWidths}
                dataSource={filtered}
                size="middle"
                scroll={{ x: "max-content" }}
                tableLayout="fixed"
                sticky
                pagination={{ pageSizeOptions: [5,10,20,50,75], showSizeChanger: true }}
                rowKey="key"
            />
        </>
    );
}
